import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, CheckCircle2, XCircle, Send, Unplug, ArrowRight, Info, KeyRound, Layers, ListChecks, Lock, RefreshCcw, Search, Shield, Timer, Trash2, Unlock, AlertTriangle, Copy } from 'lucide-react';
import { apiClient } from '../api/client';
import { useAuth } from '../hooks/useAuth';
import vaultIcon from '../assets/icons/hashicorp-vault.svg';

const VaultIntegration: React.FC = () => {
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [exampleMount, setExampleMount] = useState('');
  const [exampleChannel, setExampleChannel] = useState('');

  const [address, setAddress] = useState('');
  const [namespace, setNamespace] = useState('');
  const [pollInterval, setPollInterval] = useState<number | null>(null);
  const [lastPollAt, setLastPollAt] = useState<string | null>(null);
  const [resourcesTracked, setResourcesTracked] = useState(0);
  const [sealed, setSealed] = useState<boolean | null>(null);

  const [connectAddress, setConnectAddress] = useState('');
  const [connectToken, setConnectToken] = useState('');
  const [connectNamespace, setConnectNamespace] = useState('');
  const [copied, setCopied] = useState<string | null>(null);

  const copyValue = (key: string, value: string) => {
    navigator.clipboard.writeText(value);
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  const cliEnvSnippet = [
    `export VAULT_ADDR="${connectAddress.trim() || 'https://<your-cluster>.hashicorp.cloud:8200'}"`,
    `export VAULT_NAMESPACE="${connectNamespace.trim() || 'admin'}"`,
    'export VAULT_TOKEN="<admin token from step 1>"',
    'vault token lookup',
  ].join('\n');

  const cliPolicySnippet = `vault policy write kestrel - <<'EOF'
# Health / status / introspection (triggers + investigations)
path "sys/health"             { capabilities = ["read"] }
path "sys/seal-status"        { capabilities = ["read"] }
path "sys/mounts"             { capabilities = ["read"] }
path "sys/policies/acl"       { capabilities = ["list"] }
path "sys/policies/acl/*"     { capabilities = ["create", "read", "update"] }
path "sys/auth"               { capabilities = ["read"] }

# Leases: lookup requires sudo (Vault requirement); renew/revoke for lease blocks
path "sys/leases/lookup"      { capabilities = ["list", "sudo"] }
path "sys/leases/lookup/*"    { capabilities = ["list", "sudo"] }
path "sys/leases/renew"       { capabilities = ["update"] }
path "sys/leases/revoke"      { capabilities = ["update"] }

# Token hygiene: accessor listing requires sudo; revoke-accessor is update
path "auth/token/accessors"       { capabilities = ["list", "sudo"] }
path "auth/token/revoke-accessor" { capabilities = ["update"] }

# KV v2 (default mount "secret") — metadata for triggers/listing,
# data for read/write workflow blocks. Trim to read-only if preferred.
path "secret/metadata"        { capabilities = ["list"] }
path "secret/metadata/*"      { capabilities = ["read", "list", "delete"] }
path "secret/data/*"          { capabilities = ["create", "read", "update", "delete"] }
path "secret/delete/*"        { capabilities = ["update"] }

# Database secrets engine static-role rotation (if a database/ mount exists)
path "database/static-roles"    { capabilities = ["list"] }
path "database/rotate-role/*"   { capabilities = ["update"] }

# Token self-renewal
path "auth/token/renew-self"  { capabilities = ["update"] }
path "auth/token/lookup-self" { capabilities = ["read"] }
EOF`;

  const cliTokenSnippet = 'vault token create -policy=kestrel -period=768h -orphan -display-name=kestrel';

  const fetchStatus = async () => {
    try {
      const data = await apiClient.getVaultStatus();
      if (data?.connected) {
        setConnected(true);
        setAddress(data.address || '');
        setNamespace(data.namespace || '');
        setPollInterval(data.poll_interval ?? null);
        setLastPollAt(data.last_poll_at || null);
        setResourcesTracked(data.resources_tracked || 0);
        setSealed(typeof data.sealed === 'boolean' ? data.sealed : null);
      } else {
        setConnected(false);
      }
    } catch {
      setConnected(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) fetchStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleConnect = async () => {
    setError(null);
    setSuccessMsg(null);
    setSaving(true);
    try {
      await apiClient.connectVault({
        address: connectAddress.trim(),
        token: connectToken,
        namespace: connectNamespace.trim() || undefined,
      });
      setSuccessMsg('HashiCorp Vault connected successfully!');
      setConnectToken('');
      await fetchStatus();
    } catch (err: any) {
      setError(err.response?.data || err.message || 'Failed to connect');
    } finally {
      setSaving(false);
    }
  };

  const handleDisconnect = async () => {
    setError(null);
    setSuccessMsg(null);
    try {
      await apiClient.disconnectVault();
      setConnected(false);
      setSuccessMsg('HashiCorp Vault disconnected.');
    } catch (err: any) {
      setError(err.response?.data || 'Failed to disconnect');
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const data = await apiClient.testVault();
      if (data?.success) {
        setTestResult({ success: true, message: `Connected to Vault${data.version ? ` v${data.version}` : ''} (${data.sealed ? 'sealed' : 'unsealed'})` });
      } else {
        setTestResult({ success: false, message: data?.error || 'Connection test failed' });
      }
    } catch (err: any) {
      setTestResult({ success: false, message: err.response?.data || 'Connection test failed' });
    } finally {
      setTesting(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-140px)]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <Alert variant="destructive">
          <XCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {successMsg && (
        <Alert className="border-green-500 bg-green-50 dark:bg-green-950/30">
          <CheckCircle2 className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-700 dark:text-green-400">{successMsg}</AlertDescription>
        </Alert>
      )}
      {connected ? (
        <>
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <img src={vaultIcon} alt="HashiCorp Vault" className="w-7 h-7 object-contain" />
              <div>
                <CardTitle className="flex items-center gap-2">
                  HashiCorp Vault
                  <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 text-xs font-normal">Connected</Badge>
                  {sealed === true && (
                    <Badge className="bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 text-xs font-normal">Sealed</Badge>
                  )}
                </CardTitle>
                <CardDescription>Manage KV secrets, policies, leases, and database credential rotation; react to seal-status changes, secret updates, and stale secrets; and run AI investigations against Vault</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              {address && (
                <div className="min-w-0">
                  <span className="text-muted-foreground">Vault Address</span>
                  <p className="font-medium truncate">{address}</p>
                </div>
              )}
              {namespace && (
                <div className="min-w-0">
                  <span className="text-muted-foreground">Namespace</span>
                  <p className="font-medium truncate">{namespace}</p>
                </div>
              )}
              {pollInterval != null && (
                <div>
                  <span className="text-muted-foreground">Poll Interval</span>
                  <p className="font-medium">{pollInterval >= 60 ? `${Math.round(pollInterval / 60)}m` : `${pollInterval}s`}</p>
                </div>
              )}
              <div>
                <span className="text-muted-foreground">Resources Tracked</span>
                <p className="font-medium">{resourcesTracked.toLocaleString()}</p>
              </div>
              {lastPollAt && (
                <div>
                  <span className="text-muted-foreground">Last Poll</span>
                  <p className="font-medium">{new Date(lastPollAt).toLocaleString()}</p>
                </div>
              )}
            </div>

            {testResult && (
              <Alert variant={testResult.success ? 'default' : 'destructive'} className={testResult.success ? 'border-green-500 bg-green-50 dark:bg-green-950/30' : ''}>
                {testResult.success ? <CheckCircle2 className="h-4 w-4 text-green-600" /> : <XCircle className="h-4 w-4" />}
                <AlertDescription className={testResult.success ? 'text-green-700 dark:text-green-400' : ''}>{testResult.message}</AlertDescription>
              </Alert>
            )}

            <div className="flex flex-wrap items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleTest} disabled={testing} className="gap-1">
                {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                Test Connection
              </Button>
              <Button variant="outline" size="sm" onClick={handleDisconnect} className="gap-1">
                <Unplug className="w-3.5 h-3.5" /> Disconnect
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Available Workflow Blocks</CardTitle>
            <CardDescription>Use these Vault actions and triggers in your workflows to automate secret rotation, incident response, and security review. Vault has no outbound webhooks, so triggers poll the Vault API and diff state between cycles — secret values are never read, only KV metadata.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 mb-4">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Triggers (poll-based)</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {[
                  { name: 'Vault Sealed / Unsealed', desc: 'Triggers when Vault seals (secrets unavailable) or recovers.', Icon: Lock },
                  { name: 'Health Degraded', desc: 'Triggers when Vault becomes unreachable or unhealthy.', Icon: AlertTriangle },
                  { name: 'Secret Version Created', desc: 'Triggers when a KV v2 secret gets a new version (metadata only).', Icon: KeyRound },
                  { name: 'Secret Stale (Rotation Due)', desc: 'Triggers when a secret\u2019s latest version exceeds the age threshold.', Icon: Timer },
                  { name: 'Policy Created / Deleted', desc: 'Triggers on ACL policy lifecycle changes for security review.', Icon: Shield },
                  { name: 'Auth Method Enabled / Disabled', desc: 'Triggers when an auth method is enabled or disabled.', Icon: Unlock },
                ].map(block => (
                  <div key={block.name} className="border rounded-lg p-3 space-y-1">
                    <div className="flex items-center gap-2">
                      <block.Icon className="w-4 h-4 text-muted-foreground shrink-0" />
                      <span className="font-medium text-sm min-w-0 truncate">{block.name}</span>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">{block.desc}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Actions</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {[
                  { name: 'Read / Write / Delete Secret', desc: 'Manage KV v2 secrets. Read outputs are redacted in logs and only usable by downstream steps.', Icon: KeyRound },
                  { name: 'List Secrets / Get Metadata', desc: 'Enumerate secret paths and read version metadata without touching values.', Icon: ListChecks },
                  { name: 'Rotate Static Role', desc: 'Trigger database static-role credential rotation.', Icon: RefreshCcw },
                  { name: 'Manage Policies', desc: 'List, read, and write ACL policies from workflows.', Icon: Shield },
                  { name: 'Leases & Tokens', desc: 'List, renew, and revoke leases; list and revoke token accessors.', Icon: Trash2 },
                  { name: 'Get Health / List Mounts / Auth Methods', desc: 'Read cluster health and enumerate mounts and auth methods.', Icon: Info },
                  { name: 'Investigate Vault', desc: 'AI investigates health, mounts, policies, and secret metadata (read-only, never values).', Icon: Search },
                  { name: 'List Mounts / Policies / Auth Methods', desc: 'Enumerate the security surface for audits and reviews.', Icon: Layers },
                ].map(block => (
                  <div key={block.name} className="border rounded-lg p-3 space-y-1">
                    <div className="flex items-center gap-2">
                      <block.Icon className="w-4 h-4 text-muted-foreground shrink-0" />
                      <span className="font-medium text-sm min-w-0 truncate">{block.name}</span>
                    </div>
                    <p className="text-xs text-muted-foreground leading-relaxed">{block.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Example Workflow</CardTitle>
            <CardDescription>Click the highlighted fields below to customize, then create the workflow.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-muted/50 rounded-lg p-4 text-sm text-muted-foreground leading-loose">
              When a secret under the{' '}
              <input
                value={exampleMount}
                onChange={e => setExampleMount(e.target.value)}
                placeholder="secret/"
                style={{ width: `${Math.max(exampleMount.length || 8, 8)}ch` }}
                className="inline-block bg-cyan-200 dark:bg-cyan-800 text-cyan-900 dark:text-cyan-100 font-medium border border-cyan-400 dark:border-cyan-600 rounded px-1 py-0 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/50 placeholder:text-cyan-600 dark:placeholder:text-cyan-300"
              />{' '}mount in Vault has not been rotated in 90 days, notify{' '}
              <input
                value={exampleChannel}
                onChange={e => setExampleChannel(e.target.value)}
                placeholder="#channel"
                style={{ width: `${Math.max(exampleChannel.length || 8, 8)}ch` }}
                className="inline-block bg-cyan-200 dark:bg-cyan-800 text-cyan-900 dark:text-cyan-100 font-medium border border-cyan-400 dark:border-cyan-600 rounded px-1 py-0 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/50 placeholder:text-cyan-600 dark:placeholder:text-cyan-300"
              />{' '}on Slack with the secret path and age, and ask for approval to rotate it.
            </div>
            <Button
              size="sm"
              className="gap-1.5"
              disabled={!exampleMount || !exampleChannel}
              onClick={() => navigate('/workflows/new', {
                state: {
                  suggestion: {
                    nl_prompt: `When a secret under the ${exampleMount} mount in Vault has not been rotated in 90 days, notify ${exampleChannel} on Slack with the secret path and age, and ask for approval to rotate it.`,
                  },
                },
              })}
            >
              Create This Workflow <ArrowRight className="w-3.5 h-3.5" />
            </Button>
          </CardContent>
        </Card>
        </>
      ) : (
        <>
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <img src={vaultIcon} alt="HashiCorp Vault" className="w-7 h-7 object-contain" />
              <div>
                <CardTitle>HashiCorp Vault</CardTitle>
                <CardDescription>Connect Vault to manage KV secrets, policies, and leases, drive secret rotation workflows, react to seal-status and secret lifecycle events, and run AI investigations. Secret values are never read by triggers — only version metadata.</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="space-y-4">
              <p className="text-sm font-medium">Create a Kestrel token for Vault</p>
              <div className="flex gap-3">
                <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center text-xs font-semibold shrink-0">1</div>
                <div className="text-sm text-muted-foreground space-y-1 min-w-0">
                  <p className="text-foreground font-medium">Get a temporary admin token</p>
                  <p><strong className="text-foreground">HCP Vault Dedicated:</strong> on your cluster's <strong className="text-foreground">Overview</strong> page, under <strong className="text-foreground">Quick actions</strong>, click <strong className="text-foreground">New admin token → Generate token</strong> and copy it (valid for 6 hours — you only need it for the steps below). Also copy the <strong className="text-foreground">Public</strong> cluster URL from the <strong className="text-foreground">Cluster URLs</strong> section.</p>
                  <p><strong className="text-foreground">Self-hosted Vault:</strong> use any token with permission to write policies and create tokens (e.g. the root token in dev).</p>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center text-xs font-semibold shrink-0">2</div>
                <div className="text-sm text-muted-foreground space-y-2 min-w-0 flex-1">
                  <p className="text-foreground font-medium">Point your terminal at the cluster</p>
                  <p>Requires the Vault CLI (<code className="bg-muted px-1 rounded text-[11px] font-mono">brew install hashicorp/tap/vault</code>). Fill in the Vault Address field below and this snippet updates automatically. On self-hosted OSS Vault, skip the <code className="bg-muted px-1 rounded text-[11px] font-mono">VAULT_NAMESPACE</code> line. The final <code className="bg-muted px-1 rounded text-[11px] font-mono">vault token lookup</code> should print your token details — if it says permission denied, regenerate the admin token and paste the new value.</p>
                  <div className="relative">
                    <pre className="bg-muted rounded-md p-3 pr-12 text-[11px] font-mono overflow-x-auto whitespace-pre">{cliEnvSnippet}</pre>
                    <Button variant="outline" size="sm" onClick={() => copyValue('env', cliEnvSnippet)} className="gap-1 absolute top-2 right-2 h-7 px-2">
                      {copied === 'env' ? <CheckCircle2 className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
                    </Button>
                  </div>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center text-xs font-semibold shrink-0">3</div>
                <div className="text-sm text-muted-foreground space-y-2 min-w-0 flex-1">
                  <p className="text-foreground font-medium">Create the Kestrel policy</p>
                  <p>Grants read/list on status paths and KV metadata, plus KV read/write for secret-management blocks. Remove the <code className="bg-muted px-1 rounded text-[11px] font-mono">secret/data/*</code> write capabilities if you only want read-only workflows.</p>
                  <div className="relative">
                    <pre className="bg-muted rounded-md p-3 pr-12 text-[11px] font-mono overflow-x-auto whitespace-pre max-h-56">{cliPolicySnippet}</pre>
                    <Button variant="outline" size="sm" onClick={() => copyValue('policy', cliPolicySnippet)} className="gap-1 absolute top-2 right-2 h-7 px-2">
                      {copied === 'policy' ? <CheckCircle2 className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
                    </Button>
                  </div>
                </div>
              </div>
              <div className="flex gap-3">
                <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center text-xs font-semibold shrink-0">4</div>
                <div className="text-sm text-muted-foreground space-y-2 min-w-0 flex-1">
                  <p className="text-foreground font-medium">Create a periodic token for Kestrel</p>
                  <p>A periodic token renews indefinitely (each renewal resets a 32-day clock) instead of expiring. The <code className="bg-muted px-1 rounded text-[11px] font-mono">-orphan</code> flag is required: without it the token is a child of your temporary admin token and is revoked the moment that admin token expires (6 hours on HCP). Copy the <code className="bg-muted px-1 rounded text-[11px] font-mono">token</code> value (starts with <code className="bg-muted px-1 rounded text-[11px] font-mono">hvs.</code>) from the output into the Vault Token field below.</p>
                  <div className="relative">
                    <pre className="bg-muted rounded-md p-3 pr-12 text-[11px] font-mono overflow-x-auto whitespace-pre">{cliTokenSnippet}</pre>
                    <Button variant="outline" size="sm" onClick={() => copyValue('token', cliTokenSnippet)} className="gap-1 absolute top-2 right-2 h-7 px-2">
                      {copied === 'token' ? <CheckCircle2 className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            <div className="border-t pt-4 space-y-4">
            <div>
              <Label htmlFor="vault-address" className="text-sm">Vault Address</Label>
              <Input
                id="vault-address"
                value={connectAddress}
                onChange={e => setConnectAddress(e.target.value)}
                placeholder="https://vault.example.com:8200"
                className="mt-1 font-mono"
              />
              <p className="text-xs text-muted-foreground mt-1">
                The base URL of your Vault server, reachable from the Kestrel control plane. For HCP Vault Dedicated, use the <strong className="text-foreground">Public</strong> cluster URL from step 1.
              </p>
            </div>
            <div>
              <Label htmlFor="vault-token" className="text-sm">Vault Token</Label>
              <Input
                id="vault-token"
                type="password"
                value={connectToken}
                onChange={e => setConnectToken(e.target.value)}
                placeholder="hvs...."
                className="mt-1 font-mono"
              />
              <p className="text-xs text-muted-foreground mt-1">
                The periodic token created in step 4. Stored encrypted and only used to authenticate requests to the Vault API.
              </p>
            </div>
            <div>
              <Label htmlFor="vault-namespace" className="text-sm">Namespace (optional)</Label>
              <Input
                id="vault-namespace"
                value={connectNamespace}
                onChange={e => setConnectNamespace(e.target.value)}
                placeholder="admin"
                className="mt-1 font-mono"
              />
              <p className="text-xs text-muted-foreground mt-1">
                For Vault Enterprise or HCP Vault Dedicated, the namespace to operate in (HCP uses <code className="bg-muted px-1 rounded text-[11px] font-mono">admin</code>). Leave empty for open-source Vault.
              </p>
            </div>
            <Button onClick={handleConnect} disabled={saving || !connectAddress.trim() || !connectToken}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Connect Vault
            </Button>
            </div>
          </CardContent>
        </Card>
        </>
      )}
    </div>
  );
};

export default VaultIntegration;
